import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@perceptron/utils/cn";

import { MediaData } from "@demo/types/base";
import { isVideoExtension } from "@demo/utils/media";

import { MediaThumb } from "./MediaThumb";

/** Frosted-glass look for the rail: blur radius px, fill opacity, then filters. */
const GLASS = { frost: 24, tint: 0.45, saturation: 1.4, exposure: 0.9, highlight: 0.06 } as const;

/** Width of the vertical rail, and height of the horizontal one. */
export const RAIL_SIZE = 80;
const THUMB = 56;
const GAP = 8;
const PAD = 12;
/** How far the strip feathers out at an end it can still scroll toward. */
const FADE = 20;

interface FilmstripRailProps {
  media: MediaData[];
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Vertical docks to the left edge (desktop); horizontal to the top (mobile). */
  orientation?: "vertical" | "horizontal";
  className?: string;
}

/**
 * A persistent filmstrip docked to the output panel's edge — no collapse state
 * and no arrows, since the set itself is the navigation. It overlays the panel
 * rather than insetting it, so zoomed media passes under the frosted glass.
 */
export function FilmstripRail({
  media,
  activeIndex,
  onSelect,
  orientation = "vertical",
  className,
}: FilmstripRailProps) {
  const horizontal = orientation === "horizontal";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: true });

  // Gradient colour stops can't be transitioned, and Tailwind v3 has no
  // registered custom properties, so the mask is recomputed in JS from the
  // scroll position instead. Same result, one repaint per scroll frame.
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const offset = horizontal ? el.scrollLeft : el.scrollTop;
    const extent = horizontal ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight;
    const next = { atStart: offset <= 1, atEnd: offset >= extent - 1 };
    setEdges((prev) => (prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next));
  }, [horizontal]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [measure, media.length]);

  // Warm every asset's full-size image, so switching swaps an already-decoded
  // bitmap rather than holding the outgoing picture while the next one decodes
  // — which would briefly pair it with the incoming asset's annotations.
  useEffect(() => {
    const images = media
      .filter((item) => item.url && !isVideoExtension(item.extension))
      .map((item) => {
        const img = new window.Image();
        img.src = item.url!;
        return img;
      });
    return () => {
      for (const img of images) img.src = "";
    };
  }, [media]);

  // Keep the current thumbnail in view when the strip is longer than the rail.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const extent = horizontal ? el.clientWidth : el.clientHeight;
    const target = Math.max(activeIndex * (THUMB + GAP) - (extent - THUMB) / 2, 0);
    el.scrollTo(horizontal ? { left: target, behavior: "smooth" } : { top: target, behavior: "smooth" });
  }, [activeIndex, horizontal]);

  const maskImage = useMemo(() => {
    const lead = edges.atStart ? "black 0px" : `transparent 0px, black ${FADE}px`;
    const tail = edges.atEnd ? "black 100%" : `black calc(100% - ${FADE}px), transparent 100%`;
    return `linear-gradient(to ${horizontal ? "right" : "bottom"}, ${lead}, ${tail})`;
  }, [edges.atStart, edges.atEnd, horizontal]);

  const backdrop = `blur(${GLASS.frost}px) saturate(${GLASS.saturation}) brightness(${GLASS.exposure})`;

  return (
    <div
      className={cn(
        // No divider of its own: the player's card border is the line at the
        // seam. Two borders from two elements never match — each composites
        // over a different backdrop, and the rail's edge can land on a
        // fractional pixel — so only one is drawn.
        "absolute z-20 overflow-hidden",
        horizontal ? "inset-x-0 top-0" : "inset-y-0 left-0",
        className,
      )}
      style={{
        ...(horizontal ? { height: RAIL_SIZE } : { width: RAIL_SIZE }),
        backgroundColor: `rgba(10, 10, 10, ${GLASS.tint})`,
        backdropFilter: backdrop,
        WebkitBackdropFilter: backdrop,
      }}
    >
      {/* A faint top-light so it reads as glass rather than a flat fill. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(180deg, rgba(255,255,255,${GLASS.highlight}) 0%, rgba(255,255,255,0) 18%)`,
        }}
      />
      <div
        className={cn(
          "no-scrollbar absolute inset-0",
          horizontal ? "overflow-x-auto overflow-y-hidden" : "overflow-y-auto overflow-x-hidden",
        )}
        ref={scrollRef}
        style={{ padding: PAD, maskImage, WebkitMaskImage: maskImage }}
      >
        <div className={cn("flex", horizontal ? "w-max flex-row" : "flex-col")} style={{ gap: GAP }}>
          {media.map((item, index) => (
            <MediaThumb
              active={index === activeIndex}
              index={index}
              key={item.key}
              media={item}
              size={THUMB}
              onSelect={() => onSelect(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
